import React, { useState, useEffect } from "react";
import Spreadsheet from "react-spreadsheet";
import { FaSearchPlus, FaSearchMinus } from "react-icons/fa";

function SpreadsheetComponent({ initialData, title = "Spreadsheet" }) {
  const [data, setData] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(100); // 100% is default

  // Convert our 2D array format to react-spreadsheet format
    useEffect(() => {
    if (initialData) {
      const convertedData = initialData.map(row => 
        row.map(cell => ({
          value: cell === "" ? "" : cell.toString(),
          // Make headers and totals read-only
          readOnly: typeof cell === "string" && (cell === "" || cell === "TOTAL")
        }))
      );
      setData(convertedData);
    }
    }, [initialData]);

  const handleChange = (newData) => {
    setData(newData);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 400)); // Max 400%
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 10)); // Min 10%
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const handleSliderChange = (e) => {
    setZoomLevel(parseInt(e.target.value));
  };

  const handleSliderMouseDown = (e) => {
    // Only prevent browser navigation, not the slider functionality
    if (e.button === 1) { // Middle mouse button
    e.preventDefault();
    }
  };

  const handleSliderMouseUp = (e) => {
    // Only prevent browser navigation, not the slider functionality
    if (e.button === 1) { // Middle mouse button
    e.preventDefault();
    }
  };

  const handleSliderKeyDown = (e) => {
    // Only prevent browser navigation shortcuts, not arrow keys for slider
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
    }
  };

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
        e.preventDefault();
        handleZoomOut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "0") {
        e.preventDefault();
        handleZoomReset();
      }
    };

    // Prevent browser navigation gestures
    const handleWheel = (e) => {
      if (e.ctrlKey && e.target.closest('.zoom-controls')) {
        e.preventDefault();
      }
    };

    const handleTouchStart = (e) => {
      // Prevent swipe gestures on zoom controls
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchMove = (e) => {
      // Prevent swipe gestures on zoom controls
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchEnd = (e) => {
      // Prevent swipe gestures on zoom controls
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleGestureStart = (e) => {
      // Prevent gesture navigation
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleGestureChange = (e) => {
      // Prevent gesture navigation
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleGestureEnd = (e) => {
      // Prevent gesture navigation
      if (e.target.closest('.zoom-controls')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: false });
    window.addEventListener("gesturestart", handleGestureStart, { passive: false });
    window.addEventListener("gesturechange", handleGestureChange, { passive: false });
    window.addEventListener("gestureend", handleGestureEnd, { passive: false });
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("gesturestart", handleGestureStart);
      window.removeEventListener("gesturechange", handleGestureChange);
      window.removeEventListener("gestureend", handleGestureEnd);
    };
  }, []);

  return (
    <div style={{ 
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#f8f9fa"
    }}>
      {title && (
        <div style={{
          padding: "20px 20px 0 20px",
          flexShrink: 0
        }}>
          <h2 style={{ 
            textAlign: "center", 
            marginBottom: 20, 
            fontFamily: "Arial, sans-serif",
            color: "#2c3e50",
            fontSize: "24px",
            fontWeight: "600"
          }}>
            {title}
        </h2>
        </div>
      )}

      <div style={{
        flex: 1,
        padding: "0 20px 20px 20px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}>
        <div style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          border: "1px solid #e1e5e9",
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{
            flex: 1,
            overflow: "auto",
            padding: "20px",
            position: "relative"
          }}>
            <div style={{
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: "top left",
              width: `${100 / (zoomLevel / 100)}%`,
              height: `${100 / (zoomLevel / 100)}%`,
              minHeight: "100%",
              position: "relative"
            }}>
              <Spreadsheet
                data={data}
                onChange={handleChange}
                darkMode={false}
                columnLabels={false}
                rowLabels={false}
                className="custom-spreadsheet"
              />
            </div>
          </div>
        </div>

        {/* Excel-style Zoom Slider at Bottom Right */}
        <div 
          className="zoom-controls"
        style={{
            position: "absolute",
            bottom: "30px",
            right: "30px",
            background: "white",
            border: "1px solid #d0d7de",
            borderRadius: "6px",
            padding: "8px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            zIndex: 1000
          }}
        >
          <button
            onClick={handleZoomOut}
                  style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#24292f",
              fontSize: "12px"
            }}
            onMouseEnter={(e) => e.target.style.background = "#f6f8fa"}
            onMouseLeave={(e) => e.target.style.background = "transparent"}
            title="Zoom Out"
          >
            <FaSearchMinus size={12} />
          </button>
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <input
              type="range"
              min="10"
              max="400"
              value={zoomLevel}
              onChange={handleSliderChange}
              onMouseDown={handleSliderMouseDown}
              onMouseUp={handleSliderMouseUp}
              onKeyDown={handleSliderKeyDown}
                      style={{
                width: "80px",
                height: "4px",
                background: "#e1e5e9",
                outline: "none",
                borderRadius: "2px",
                cursor: "pointer"
              }}
              title={`${zoomLevel}%`}
            />
            <span style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#24292f",
              minWidth: "35px",
              textAlign: "center"
            }}>
              {zoomLevel}%
            </span>
          </div>
          
          <button
            onClick={handleZoomIn}
                      style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#24292f",
              fontSize: "12px"
            }}
            onMouseEnter={(e) => e.target.style.background = "#f6f8fa"}
            onMouseLeave={(e) => e.target.style.background = "transparent"}
            title="Zoom In"
          >
            <FaSearchPlus size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default SpreadsheetComponent;
